import {
  fetchAdminSubscriptionHistoryApi,
  fetchAdminSubscriptionsApi,
  onOrganizationCollectionFind,
  updateAdminSubscriptionOrganizationApi,
} from '../../util/api';

const ns = 'app/AdminPanelSubscriptionsPage';

export const FETCH_SUBSCRIPTIONS_REQUEST = `${ns}/FETCH_SUBSCRIPTIONS_REQUEST`;
export const FETCH_SUBSCRIPTIONS_SUCCESS = `${ns}/FETCH_SUBSCRIPTIONS_SUCCESS`;
export const FETCH_SUBSCRIPTIONS_ERROR = `${ns}/FETCH_SUBSCRIPTIONS_ERROR`;
export const MUTATE_SUBSCRIPTION_REQUEST = `${ns}/MUTATE_SUBSCRIPTION_REQUEST`;
export const MUTATE_SUBSCRIPTION_SUCCESS = `${ns}/MUTATE_SUBSCRIPTION_SUCCESS`;
export const MUTATE_SUBSCRIPTION_ERROR = `${ns}/MUTATE_SUBSCRIPTION_ERROR`;
export const FETCH_HISTORY_REQUEST = `${ns}/FETCH_HISTORY_REQUEST`;
export const FETCH_HISTORY_SUCCESS = `${ns}/FETCH_HISTORY_SUCCESS`;
export const FETCH_HISTORY_ERROR = `${ns}/FETCH_HISTORY_ERROR`;
export const FETCH_ORGANIZATIONS_REQUEST = `${ns}/FETCH_ORGANIZATIONS_REQUEST`;
export const FETCH_ORGANIZATIONS_SUCCESS = `${ns}/FETCH_ORGANIZATIONS_SUCCESS`;
export const FETCH_ORGANIZATIONS_ERROR = `${ns}/FETCH_ORGANIZATIONS_ERROR`;

const getErrorMessage = error =>
  error?.message || error?.error || 'Something went wrong. Please try again.';

const DEFAULT_SUBSCRIPTION_FILTERS = {
  page: 1,
  perPage: 10,
  keyword: '',
  organization: '',
  platform: 'all',
  status: '',
  chargeDateFrom: '',
  chargeDateTo: '',
};

let subscriptionsFetchRequestId = 0;

const toPositivePage = value => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getSubscriptionsParamsFromSearch = search => {
  const params = new URLSearchParams(search || '');
  return {
    ...DEFAULT_SUBSCRIPTION_FILTERS,
    page: toPositivePage(params.get('page')),
    keyword: params.get('keyword') || '',
    organization: params.get('organization') || '',
    platform: params.get('platform') || 'all',
    status: params.get('status') || '',
    chargeDateFrom: params.get('chargeDateFrom') || '',
    chargeDateTo: params.get('chargeDateTo') || '',
  };
};

const initialState = {
  subscriptions: [],
  pagination: null,
  fetchInProgress: false,
  fetchError: null,
  activeFetchRequestId: null,
  mutationInProgress: false,
  mutationError: null,
  history: [],
  activeHistorySubscription: null,
  historyInProgress: false,
  historyError: null,
  organizations: [],
  organizationsInProgress: false,
  organizationsError: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
        activeFetchRequestId: action.meta?.requestId,
      };
    case FETCH_SUBSCRIPTIONS_SUCCESS:
      if (action.meta?.requestId !== state.activeFetchRequestId) {
        return state;
      }
      return {
        ...state,
        fetchInProgress: false,
        activeFetchRequestId: null,
        subscriptions: action.payload.data || [],
        pagination: action.payload.pagination || null,
      };
    case FETCH_SUBSCRIPTIONS_ERROR:
      if (action.meta?.requestId !== state.activeFetchRequestId) {
        return state;
      }
      return {
        ...state,
        fetchInProgress: false,
        fetchError: action.payload,
        activeFetchRequestId: null,
      };
    case MUTATE_SUBSCRIPTION_REQUEST:
      return { ...state, mutationInProgress: true, mutationError: null };
    case MUTATE_SUBSCRIPTION_SUCCESS:
      return { ...state, mutationInProgress: false };
    case MUTATE_SUBSCRIPTION_ERROR:
      return { ...state, mutationInProgress: false, mutationError: action.payload };
    case FETCH_HISTORY_REQUEST:
      return {
        ...state,
        historyInProgress: true,
        historyError: null,
        history: [],
        activeHistorySubscription: null,
      };
    case FETCH_HISTORY_SUCCESS:
      return {
        ...state,
        historyInProgress: false,
        history: action.payload?.data || [],
        activeHistorySubscription: action.payload?.activeSubscription || null,
      };
    case FETCH_HISTORY_ERROR:
      return { ...state, historyInProgress: false, historyError: action.payload };
    case FETCH_ORGANIZATIONS_REQUEST:
      return { ...state, organizationsInProgress: true, organizationsError: null };
    case FETCH_ORGANIZATIONS_SUCCESS:
      return {
        ...state,
        organizationsInProgress: false,
        organizations: action.payload?.data || [],
      };
    case FETCH_ORGANIZATIONS_ERROR:
      return { ...state, organizationsInProgress: false, organizationsError: action.payload };
    default:
      return state;
  }
}

export const fetchAdminSubscriptions = params => async dispatch => {
  const requestId = subscriptionsFetchRequestId + 1;
  subscriptionsFetchRequestId = requestId;
  dispatch({ type: FETCH_SUBSCRIPTIONS_REQUEST, meta: { requestId } });
  try {
    const response = await fetchAdminSubscriptionsApi(params);
    const payload = {
      data: response?.data || [],
      pagination: response?.pagination || null,
    };
    dispatch({ type: FETCH_SUBSCRIPTIONS_SUCCESS, payload, meta: { requestId } });
    return payload;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: FETCH_SUBSCRIPTIONS_ERROR, payload: message, meta: { requestId } });
    throw error;
  }
};

export const updateAdminSubscriptionOrganization = (payload, params) => async dispatch => {
  dispatch({ type: MUTATE_SUBSCRIPTION_REQUEST });
  try {
    const response = await updateAdminSubscriptionOrganizationApi(payload);
    dispatch({ type: MUTATE_SUBSCRIPTION_SUCCESS });
    await dispatch(fetchAdminSubscriptions(params));
    return response;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: MUTATE_SUBSCRIPTION_ERROR, payload: message });
    throw error;
  }
};

export const fetchAdminSubscriptionHistory = params => async dispatch => {
  dispatch({ type: FETCH_HISTORY_REQUEST });
  try {
    const response = await fetchAdminSubscriptionHistoryApi(params);
    const payload = {
      data: response?.data || [],
      activeSubscription: response?.activeSubscription || null,
    };
    dispatch({ type: FETCH_HISTORY_SUCCESS, payload });
    return payload;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: FETCH_HISTORY_ERROR, payload: message });
    throw error;
  }
};

export const fetchAdminSubscriptionOrganizations = () => async dispatch => {
  dispatch({ type: FETCH_ORGANIZATIONS_REQUEST });
  try {
    const response = await onOrganizationCollectionFind({
      payload: { isDelete: false },
      pageParams: { page: 1, perPage: 1000 },
      sort: 'name_asc',
    });
    dispatch({ type: FETCH_ORGANIZATIONS_SUCCESS, payload: response });
    return response;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: FETCH_ORGANIZATIONS_ERROR, payload: message });
    throw error;
  }
};

export const loadData = (params, search) => dispatch =>
  Promise.all([
    dispatch(fetchAdminSubscriptions(getSubscriptionsParamsFromSearch(search))),
    dispatch(fetchAdminSubscriptionOrganizations()),
  ]);
